import React, { useState, useEffect } from 'react';
import { Bell, X, Download } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';

const NotificationPrompt = () => {
  const notificationContext = useNotifications();
  const { showPermissionPrompt, requestPermission, dismissPrompt } = notificationContext || {};
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  // Capture the beforeinstallprompt event for PWA install
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show install banner on mobile if not already installed
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
      if (!isStandalone) {
        setShowInstallBanner(true);
      }
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowInstallBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleEnable = async () => {
    await requestPermission();
  };

  if (!showPermissionPrompt && !showInstallBanner) return null;

  return (
    <>
      {/* Notification Permission Prompt */}
      {showPermissionPrompt && (
        <div className="fixed bottom-4 right-4 z-[60] max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 animate-in slide-in-from-bottom-4" data-testid="notification-prompt">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-rose-50 rounded-xl flex-shrink-0">
              <Bell className="w-5 h-5 text-rose-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800 text-sm">Enable Notifications</p>
              <p className="text-xs text-gray-500 mt-0.5">Get instant alerts for new messages, collab requests, and approvals.</p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleEnable}
                  className="px-3 py-1.5 bg-gradient-to-r from-rose-500 to-orange-500 text-white text-xs font-medium rounded-lg hover:shadow-md transition-shadow"
                  data-testid="enable-notifications-btn"
                >
                  Enable
                </button>
                <button
                  onClick={dismissPrompt}
                  className="px-3 py-1.5 text-gray-500 text-xs hover:text-gray-700"
                  data-testid="dismiss-notifications-btn"
                >
                  Not now
                </button>
              </div>
            </div>
            <button onClick={dismissPrompt} className="text-gray-300 hover:text-gray-500">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* PWA Install Banner (mobile) */}
      {showInstallBanner && !showPermissionPrompt && (
        <div className="fixed bottom-4 right-4 z-[60] max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 animate-in slide-in-from-bottom-4" data-testid="pwa-install-prompt">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-orange-50 rounded-xl flex-shrink-0">
              <Download className="w-5 h-5 text-orange-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800 text-sm">Install VirtualFit</p>
              <p className="text-xs text-gray-500 mt-0.5">Add to your home screen for the best experience with push notifications.</p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleInstall}
                  className="px-3 py-1.5 bg-gradient-to-r from-rose-500 to-orange-500 text-white text-xs font-medium rounded-lg hover:shadow-md transition-shadow"
                  data-testid="install-pwa-btn"
                >
                  Install
                </button>
                <button
                  onClick={() => setShowInstallBanner(false)}
                  className="px-3 py-1.5 text-gray-500 text-xs hover:text-gray-700"
                >
                  Not now
                </button>
              </div>
            </div>
            <button onClick={() => setShowInstallBanner(false)} className="text-gray-300 hover:text-gray-500">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationPrompt;
